import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formats recipe source for display
 * If source is 'manual', returns 'Calorie Science'
 * Otherwise returns the source name directly (capitalized)
 */
export function formatRecipeSource(source: string | undefined | null): string {
  if (!source) return 'Unknown';
  
  const normalizedSource = source.toLowerCase().trim();
  
  if (normalizedSource === 'manual') {
    return 'Calorie Science';
  }
  
  // Capitalize first letter of each word
  return source
    .split(/[\s_-]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Formats ingredient names consistently for display while keeping API/edit values unchanged.
 */
export function formatIngredientDisplayName(name: string | undefined | null): string {
  const normalized = String(name ?? '').replace(/\s+/g, ' ').trim();
  if (!normalized) return '';

  return normalized
    .split(' ')
    .map((word) =>
      word
        .split(/([-/])/)
        .map((part) => {
          if (part === '-' || part === '/') return part;
          if (!part) return part;
          if (/^[A-Z0-9&]{2,}$/.test(part)) return part;

          return part
            .split("'")
            .map((segment) => (
              segment
                ? segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase()
                : segment
            ))
            .join("'");
        })
        .join('')
    )
    .join(' ');
}

/**
 * Converts 24-hour time format (HH:mm) to 12-hour format with AM/PM
 * @param time24 - Time in 24-hour format (e.g., "14:30" or "09:00")
 * @returns Time in 12-hour format (e.g., "2:30 PM" or "9:00 AM")
 */
export function convertTo12HourFormat(time24: string): string {
  if (!time24 || time24 === 'N/A') return time24;
  
  // Handle formats like "14:30", "14:30:00", etc.
  const timeMatch = time24.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!timeMatch) return time24; // Return as-is if format doesn't match
  
  let hours = parseInt(timeMatch[1], 10);
  const minutes = timeMatch[2];
  const period = hours >= 12 ? 'PM' : 'AM';
  
  // Convert to 12-hour format
  if (hours === 0) {
    hours = 12; // Midnight
  } else if (hours > 12) {
    hours = hours - 12;
  }
  
  return `${hours}:${minutes} ${period}`;
}
